import { semTiposRepetidos } from './tiposHomonimos';
import { chaveSegmentoFiltro, segmentoFiltroCasaNome } from './FunctionsGlobals';
import { usaHifenLiteral } from './hifenUrl';

type SiteLike = {
  id?: string | number;
  titulo?: string;
  meta_description?: string;
  transacoes?: string[];
  Dados?: {
    empresa?: string;
    cidade?: string;
  };
};

type NamedItem = {
  nome?: string;
  label?: string;
};

type ImoveisSeoOverrides = {
  transaction?: string;
  city?: string;
  districts?: string[];
  types?: string[];
  company?: string;
  category?: string;
  code?: string;
  dormitorios?: unknown;
  suites?: unknown;
  banheiros?: unknown;
  vagas?: unknown;
  promocao?: boolean;
  favoritos?: boolean;
};

type ImoveisSeoInput = {
  asPath?: string;
  site?: SiteLike | null;
  allDataHome?: {
    cidades?: NamedItem[];
    tipos?: NamedItem[];
  } | null;
  query?: Record<string, unknown>;
  overrides?: ImoveisSeoOverrides;
  // segmento da URL (chaveSegmentoFiltro) → nome real; só vem no gate utils/hifenUrl (cs2588)
  rotulos?: Record<string, string>;
};

type ParsedPath = {
  transaction: string;
  city: string;
  districts: string[];
  company: string;
  types: string[];
  code: string;
  isNovidades: boolean;
};

const TRANSACTION_TEXT: Record<string, string> = {
  venda: 'à venda',
  aluguel: 'para alugar',
  temporada: 'para temporada',
};

const SMALL_WORDS = [
  'a',
  'o',
  'as',
  'os',
  'de',
  'da',
  'do',
  'das',
  'dos',
  'e',
  'em',
  'no',
  'na',
  'nos',
  'nas',
  'para',
  'por',
  'com',
];

const cleanText = (value: unknown) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const normalizeLookup = (value: unknown) =>
  cleanText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const toTitleCase = (value: string) => {
  const normalized = cleanText(value).toLowerCase();
  if (!normalized) return '';

  return normalized
    .split(' ')
    .map((word, index) => {
      if (index > 0 && SMALL_WORDS.includes(word)) return word;
      return word.charAt(0).toLocaleUpperCase('pt-BR') + word.slice(1);
    })
    .join(' ');
};

const decodeSlugLabel = (value: string) =>
  toTitleCase(
    safeDecode(value)
      .replace(/---/g, '-002D-')
      .replace(/-/g, ' ')
      .replace(/002D/gi, '-')
      .replace(/\+/g, ' ')
  );

// `segment`/`codsite`: no gate de hífen literal (utils/hifenUrl) o segmento "xangri-la" casa
// "Xangri-lá" pelo slug — o de-slug acima o transforma em "Xangri La".
// Tenants cuja marca é de CORRETOR (pessoa): o nome sai puro, sem o "Imobiliária" que getBrandName prefixa, e o
// artigo é masculino (ver `art` em buildImoveisSeo). cs2681 "O Cardoso Corretor de Imóveis Rurais reúne…" (03/11).
const MARCA_MASCULINA = new Set<number>([2681]);

const labelFromKnownList = (label: string, knownItems?: NamedItem[], segment?: string, codsite?: unknown) => {
  const key = normalizeLookup(label);
  if (!key || !Array.isArray(knownItems)) return '';

  const found = knownItems.find((item) => {
    const itemLabel = item?.nome || item?.label || '';
    return normalizeLookup(itemLabel) === key
      || (usaHifenLiteral(codsite) && segmentoFiltroCasaNome(segment, itemLabel, codsite));
  });

  return cleanText(found?.nome || found?.label || '');
};

const labelsFromSegment = (segment: string | undefined, knownItems?: NamedItem[], codsite?: unknown, rotulos?: Record<string, string>) => {
  if (!segment || segment === '-') return [];

  return segment
    .split('+')
    .map((part) => {
      const rotulo = rotulos?.[chaveSegmentoFiltro(part)];
      if (rotulo) return cleanText(rotulo);
      const decoded = decodeSlugLabel(part);
      return labelFromKnownList(decoded, knownItems, part, codsite) || decoded;
    })
    .filter(Boolean);
};

const normalizeTransaction = (value: unknown, site?: SiteLike | null) => {
  const raw = normalizeLookup(value);
  if (raw === 'aluguel' || raw === 'locacao' || raw === 'locação' || raw === 'rent') return 'aluguel';
  if (raw === 'temporada') return 'temporada';
  if (raw === 'venda' || raw === 'sale') return 'venda';

  const firstTransaction = normalizeLookup(site?.transacoes?.[0]);
  if (firstTransaction === 'aluguel' || firstTransaction === 'locacao' || firstTransaction === 'locação') {
    return 'aluguel';
  }
  if (firstTransaction === 'temporada') return 'temporada';
  return 'venda';
};

const parsePath = (asPath: string | undefined, allDataHome?: ImoveisSeoInput['allDataHome'], site?: SiteLike | null, rotulos?: Record<string, string>): ParsedPath => {
  const path = String(asPath || '/imoveis').split('?')[0].split('#')[0];
  const parts = path.split('/').filter(Boolean);
  const model = parts[1] || '';
  const isNovidades = model === 'novidades';

  return {
    transaction: normalizeTransaction(model, site),
    city: labelsFromSegment(parts[2], allDataHome?.cidades, site?.id, rotulos)[0] || '',
    districts: labelsFromSegment(parts[3], undefined, undefined, rotulos),
    company: labelsFromSegment(parts[4], undefined, undefined, rotulos)[0] || '',
    types: labelsFromSegment(parts[5], allDataHome?.tipos, site?.id, rotulos),
    code: labelsFromSegment(parts[6])[0] || '',
    isNovidades,
  };
};

const normalizeLabelList = (value: string[] | undefined) =>
  Array.isArray(value) ? value.map((item) => cleanText(item)).filter(Boolean) : [];

const firstClean = (...values: unknown[]) => {
  for (const value of values) {
    const cleaned = cleanText(value);
    if (cleaned) return cleaned;
  }
  return '';
};

const formatList = (values: string[]) => {
  const list = values.map((item) => cleanText(item)).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} e ${list[1]}`;
  return `${list[0]}, ${list[1]} e ${list[2]}`;
};

const pluralizeType = (value: string) => {
  const label = cleanText(value);
  if (!label) return '';

  return label
    .replace(/^Casa\b/i, 'Casas')
    .replace(/^Apartamento\b/i, 'Apartamentos')
    .replace(/^Terreno\b/i, 'Terrenos')
    .replace(/^Sobrado\b/i, 'Sobrados')
    .replace(/^Cobertura\b/i, 'Coberturas')
    .replace(/^Sala\b/i, 'Salas')
    .replace(/^Loft\b/i, 'Lofts')
    .replace(/^Chácara\b/i, 'Chácaras')
    .replace(/^Fazenda\b/i, 'Fazendas')
    .replace(/^Lote\b/i, 'Lotes');
};

const getBrandName = (site?: SiteLike | null) => {
  const candidates = [site?.titulo, site?.Dados?.empresa].map(cleanText).filter(Boolean);
  const withImobiliaria = candidates.find((item) => /imobili[aá]ri/i.test(item));
  const source = withImobiliaria || candidates[0] || 'Imobiliária';

  if (/imobili[aá]ri/i.test(source)) return source;
  if (MARCA_MASCULINA.has(Number(site?.id))) return source;
  return `Imobiliária ${source}`;
};

const getQueryValue = (
  key: string,
  query?: Record<string, unknown>,
  searchParams?: URLSearchParams,
  override?: unknown
) => {
  if (override !== undefined && override !== null && override !== '') return override;
  const fromQuery = query?.[key];
  if (Array.isArray(fromQuery)) return fromQuery[0];
  if (fromQuery !== undefined && fromQuery !== null && fromQuery !== '') return fromQuery;
  return searchParams?.get(key) || '';
};

const toPositiveInt = (value: unknown) => {
  const source = Array.isArray(value) ? value[0] : value;
  const parsed = Number(String(source || '').replace(/[^\d]/g, ''));
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed;
};

const getMainFeatureText = (
  query?: Record<string, unknown>,
  searchParams?: URLSearchParams,
  overrides?: ImoveisSeoOverrides
) => {
  // Atelier (utils/qtdMultiSelecao): "2,3" = vários números marcados → "com 2 ou 3 dormitórios"
  // (antes a vírgula sumia e virava "23"). Sem vírgula, o número de sempre.
  const dormitorios = toPositiveInts(getQueryValue('dormitorios', query, searchParams, overrides?.dormitorios));
  const suites = toPositiveInts(getQueryValue('suites', query, searchParams, overrides?.suites));
  const banheiros = toPositiveInts(getQueryValue('banheiros', query, searchParams, overrides?.banheiros));
  const vagas = toPositiveInts(getQueryValue('vagas', query, searchParams, overrides?.vagas));

  if (dormitorios.length) return `com ${juntarOu(dormitorios)} ${dormitorios.join() === '1' ? 'dormitório' : 'dormitórios'}`;
  if (suites.length) return `com ${juntarOu(suites)} ${suites.join() === '1' ? 'suíte' : 'suítes'}`;
  if (banheiros.length) return `com ${juntarOu(banheiros)} ${banheiros.join() === '1' ? 'banheiro' : 'banheiros'}`;
  if (vagas.length) return `com ${juntarOu(vagas)} ${vagas.join() === '1' ? 'vaga' : 'vagas'}`;
  return '';
};

const toPositiveInts = (value: unknown): number[] => {
  const source = Array.isArray(value) ? value[0] : value;
  const partes = String(source ?? '').split(',').map((parte) => toPositiveInt(parte)).filter((n) => n > 0);
  return Array.from(new Set(partes)).sort((a, b) => a - b);
};

const juntarOu = (lista: number[]) =>
  lista.length > 1 ? `${lista.slice(0, -1).join(', ')} ou ${lista[lista.length - 1]}` : String(lista[0]);

const decapitalize = (value: string) => {
  const cleaned = cleanText(value);
  if (!cleaned) return '';
  return cleaned.charAt(0).toLocaleLowerCase('pt-BR') + cleaned.slice(1);
};

const limitMetaDescription = (value: string, maxLength = 180) => {
  const cleaned = cleanText(value);
  if (cleaned.length <= maxLength) return cleaned;

  const sliced = cleaned.slice(0, maxLength - 1);
  const lastSpace = sliced.lastIndexOf(' ');
  const safeSlice = lastSpace > 120 ? sliced.slice(0, lastSpace) : sliced;
  return `${safeSlice.replace(/[,.]$/, '')}.`;
};

// cs2669 (Daniel, 29/10): o H1 da listagem de venda SEM filtro (tipo, categoria, bairro, cidade, quantidade) vira
// "Imóveis à venda/locação". Com filtro, o título segue montado como sempre. Só nesta licença; zero diff para os demais.
export const tituloListagemDoSite = (site: any, titulo: string, sufixo = ''): string =>
  Number(site?.id) === 2669 && !cleanText(sufixo) && /^Im[óo](veis|vel) à venda$/.test(cleanText(titulo))
    ? 'Imóveis à venda/locação'
    : titulo;

export const buildImoveisSeo = ({
  asPath,
  site,
  allDataHome,
  query,
  overrides,
  rotulos,
}: ImoveisSeoInput) => {
  const parsed = parsePath(asPath, allDataHome, site, rotulos);
  const searchParams = new URLSearchParams(String(asPath || '').split('?')[1] || '');
  // cs899 (20/10): o site é do Condomínio Moradas Club Canoas, não de uma imobiliária.
  const isMoradasClub = Number(site?.id) === 899;
  const brand = isMoradasClub ? 'Moradas Club Canoas' : getBrandName(site);
  // cs2681 (Cardoso Corretor de Imóveis Rurais, 03/11): a marca é MASCULINA — "O Cardoso … reúne", "com o Cardoso".
  // Os demais sites seguem com o artigo feminino de sempre (byte a byte igual).
  const art = MARCA_MASCULINA.has(Number(site?.id))
    ? { A: 'O', a: 'o', na: 'no' }
    : { A: 'A', a: 'a', na: 'na' };

  if (parsed.isNovidades) {
    const heading = 'Novidades em imóveis';
    return {
      title: `${heading} | ${brand}`,
      heading,
      description: limitMetaDescription(
        `Confira novidades em imóveis com ${art.a} ${brand}. Veja oportunidades recentes, compare opções por cidade, bairro e valor, e fale com um corretor especialista.`
      ),
      intro: `Confira novidades em imóveis com opções recentes para morar ou investir. ${art.A} ${brand} reúne oportunidades por localização, valor e características para facilitar sua escolha.`,
    };
  }

  const transaction = normalizeTransaction(overrides?.transaction || parsed.transaction, site);
  const transactionText = TRANSACTION_TEXT[transaction] || TRANSACTION_TEXT.venda;
  const city = firstClean(overrides?.city, parsed.city);
  const districts = normalizeLabelList(overrides?.districts);
  const selectedDistricts = districts.length > 0 ? districts : parsed.districts;
  const types = normalizeLabelList(overrides?.types);
  const selectedTypes = semTiposRepetidos(types.length > 0 ? types : parsed.types, site?.id);
  const company = firstClean(overrides?.company, parsed.company);
  const category = firstClean(overrides?.category);
  const code = firstClean(overrides?.code, parsed.code);
  const isPromocao = Boolean(overrides?.promocao || getQueryValue('promocao', query, searchParams) === '1');
  const isFavoritos = Boolean(overrides?.favoritos || getQueryValue('favoritos', query, searchParams));

  if (code) {
    const heading = `Imóvel código ${code}`;
    return {
      title: `${heading} | ${brand}`,
      heading,
      description: limitMetaDescription(
        `Veja informações do imóvel código ${code} com ${art.a} ${brand}. Consulte disponibilidade, localização, valor e fale com um corretor especialista.`
      ),
      intro: `Consulte as informações do imóvel código ${code}, compare características e fale com ${art.a} ${brand} para confirmar disponibilidade, valor e condições de negociação.`,
    };
  }

  if (isFavoritos) {
    const heading = 'Imóveis favoritos';
    return {
      title: `${heading} | ${brand}`,
      heading,
      description: limitMetaDescription(
        `Veja sua seleção de imóveis favoritos ${art.na} ${brand} e compare opções por localização, valor, dormitórios e características.`
      ),
      intro: `Veja sua seleção de imóveis favoritos e compare opções por localização, valor, dormitórios e características antes de falar com ${art.a} ${brand}.`,
    };
  }

  const subjectBase = selectedTypes.length > 0
    ? formatList(selectedTypes.map(pluralizeType))
    : 'Imóveis';
  const subject = category ? `${subjectBase} ${category}` : subjectBase;
  const districtText = formatList(selectedDistricts);
  const locationParts = [
    company ? `no empreendimento ${company}` : '',
    districtText ? `${selectedDistricts.length > 1 ? 'nos bairros' : 'no bairro'} ${districtText}` : '',
    city ? `em ${city}` : '',
  ].filter(Boolean);
  const location = locationParts.join(', ');
  const featureText = getMainFeatureText(query, searchParams, overrides);

  const heading = cleanText(
    `${isPromocao ? `${subject} em promoção` : `${subject} ${transactionText}`} ${location} ${featureText}`
  );
  const subjectDescription = decapitalize(subject);
  const searchIntent = cleanText(
    `${subjectDescription} ${isPromocao ? 'em promoção' : transactionText} ${location}`
  );
  const filterLocationText = city || districtText ? 'bairro, valor e dormitórios' : 'cidade, bairro, valor e dormitórios';
  // cs834 (Cecília, Daniel 21/10): frase fixa da marca no lugar do "Confira … para morar ou investir".
  const intro = Number(site?.id) === 834
    ? 'Imóveis de médio e alto padrão, com atendimento humanizado e personalizado.'
    // cs2669 (Daniel, 29/10): texto da plataforma (rede de corretores) em toda a listagem.
    : Number(site?.id) === 2669
    ? 'Confira imóveis à venda e/ou locação. A plataforma B2B Imóveis Ceará reúne imóveis de corretores e corretoras, em diferentes localidades, com filtro por valor, dormitórios e demais características, para facilitar a sua busca.'
    : Number(site?.id) === 2582
    ? cleanText(
      `Confira os imóveis à venda para morar ou investir. O seu patrimônio merece uma decisão inteligente Aqui você encontra imóveis em diferentes localidades, com filtros por valor, localização, dormitórios e características para facilitar sua escolha.`
    )
    : Number(site?.id) === 1630
      ? 'Confira imóveis à venda no Sul da Ilha para morar ou investir com a Prospect Imóveis. Encontre oportunidades em diferentes regiões, com filtros inteligentes por valor, localização, número de dormitórios, condomínios e loteamentos — tudo para tornar sua busca mais prática, rápida e assertiva.'
      : isMoradasClub
        ? cleanText(
          `Confira ${subjectDescription} ${isPromocao ? 'em promoção' : transactionText} no Condomínio Moradas Club Canoas para morar ou investir.`
        )
        : cleanText(
        `Confira ${searchIntent} para morar ou investir. ${art.A} ${brand} reúne imóveis em diferentes localidades, com filtros por valor, localização, dormitórios e características para facilitar sua escolha.`
      );

  return {
    title: `${heading} | ${brand}`,
    heading,
    description: limitMetaDescription(
      isMoradasClub
        ? `Encontre ${subjectDescription} ${isPromocao ? 'em promoção' : transactionText} no Condomínio Moradas Club Canoas. Veja imóveis selecionados, compare opções por ${filterLocationText} e fale com um corretor especialista.`
        : `Encontre ${searchIntent} com ${art.a} ${brand}. Veja imóveis selecionados, compare opções por ${filterLocationText} e fale com um corretor especialista.`
    ),
    intro,
  };
};
